import { useEffect, useState } from 'react';
import { Alert, Button, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import type { Tenant } from '../../app/types';
import PageSection from '../shared/PageSection';

interface AdminPageProps {
  readonly tenant: Tenant | null;
  readonly onSave: (payload: {
    collectionMode: 'shared' | 'dedicated';
    sharedCollectionId?: string;
    dedicatedCollectionId?: string;
    matchThreshold: number;
    retentionDays: number;
    identityVerificationEnabled: boolean;
    identityVerificationThreshold: number;
  }) => Promise<void>;
}

export default function AdminPage({ tenant, onSave }: AdminPageProps) {
  const [collectionMode, setCollectionMode] = useState<'shared' | 'dedicated'>('shared');
  const [sharedCollectionId, setSharedCollectionId] = useState('');
  const [dedicatedCollectionId, setDedicatedCollectionId] = useState('');
  const [matchThreshold, setMatchThreshold] = useState(90);
  const [retentionDays, setRetentionDays] = useState(30);
  const [identityVerificationEnabled, setIdentityVerificationEnabled] = useState(true);
  const [identityVerificationThreshold, setIdentityVerificationThreshold] = useState(90);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenant) {
      return;
    }

    setCollectionMode(tenant.settings.collectionMode);
    setSharedCollectionId(tenant.settings.sharedCollectionId ?? '');
    setDedicatedCollectionId(tenant.settings.dedicatedCollectionId ?? '');
    setMatchThreshold(tenant.settings.matchThreshold);
    setRetentionDays(tenant.settings.retentionDays);
    setIdentityVerificationEnabled(tenant.settings.identityVerificationEnabled);
    setIdentityVerificationThreshold(tenant.settings.identityVerificationThreshold);
  }, [tenant]);

  const submit = async () => {
    try {
      setError('');
      await onSave({
        collectionMode,
        sharedCollectionId: collectionMode === 'shared' ? sharedCollectionId || undefined : undefined,
        dedicatedCollectionId: collectionMode === 'dedicated' ? dedicatedCollectionId || undefined : undefined,
        matchThreshold,
        retentionDays,
        identityVerificationEnabled,
        identityVerificationThreshold,
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to save tenant settings.');
    }
  };

  return (
    <PageSection title="Tenant Settings" subtitle="Collection strategy, detection policy, and identity verification controls for the active tenant.">
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <TextField
          select
          label="Collection Mode"
          value={collectionMode}
          onChange={(event) => setCollectionMode(event.target.value as 'shared' | 'dedicated')}
        >
          <MenuItem value="shared">Shared</MenuItem>
          <MenuItem value="dedicated">Dedicated</MenuItem>
        </TextField>
        {collectionMode === 'shared' ? (
          <TextField
            label="Shared Collection ID"
            value={sharedCollectionId}
            onChange={(event) => setSharedCollectionId(event.target.value)}
          />
        ) : (
          <TextField
            label="Dedicated Collection ID"
            value={dedicatedCollectionId}
            onChange={(event) => setDedicatedCollectionId(event.target.value)}
          />
        )}
        <TextField
          type="number"
          label="Match Threshold"
          value={matchThreshold}
          onChange={(event) => setMatchThreshold(Number(event.target.value))}
        />
        <TextField
          type="number"
          label="Retention Days"
          value={retentionDays}
          onChange={(event) => setRetentionDays(Number(event.target.value))}
        />
        <FormControlLabel
          control={
            <Switch
              checked={identityVerificationEnabled}
              onChange={(event) => setIdentityVerificationEnabled(event.target.checked)}
            />
          }
          label="Enable Identity Verification"
        />
        <TextField
          type="number"
          label="Identity Verification Threshold"
          value={identityVerificationThreshold}
          onChange={(event) => setIdentityVerificationThreshold(Number(event.target.value))}
        />
        <Button variant="contained" onClick={() => void submit()} disabled={!tenant}>
          Save Settings
        </Button>
      </Stack>
    </PageSection>
  );
}
